import { __ } from "@wordpress/i18n";
import {
	InspectorControls,
	useBlockProps,
	PanelColorSettings,
} from "@wordpress/block-editor";
import {
	PanelBody,
	ToggleControl,
	QueryControls,
	SelectControl,
} from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import { getActiveAddonComponent } from "./addonHelpers";

export default function Edit({ attributes, setAttributes }) {
	const {
		postType,
		numberOfPosts,
		displayPlace,
		displayTime,
		order,
		orderBy,
		style,
		displayContent,
		bgColor,
		titleColor,
	} = attributes;

	const eventPostsRaw = useSelect(
		(select) => {
			return select("core").getEntityRecords("postType", postType, {
				per_page: -1,
				_embed: true,
				order,
				orderby: orderBy,
			});
		},
		[numberOfPosts, order, orderBy],
	);
	// Sort eventPosts based on the start_date field
	const eventPosts = eventPostsRaw
		? [...eventPostsRaw]
			.filter((post) => {
				const postDate = new Date(post.start_date);
				const today = new Date();
				today.setHours(0, 0, 0, 0); // Reset time to midnight

				return postDate > today; // Only include posts with start_date in the future
			})
			.sort((a, b) => {
				const dateA = new Date(a.start_date);
				const dateB = new Date(b.start_date);
				return dateA - dateB; // Ascending order
			})
			.slice(0, numberOfPosts)  // Limit the posts according to per_page value
		: [];

	const onDisplayTime = (value) => {
		setAttributes({ displayTime: value });
	};

	const onDisplayPlace = (value) => {
		setAttributes({ displayPlace: value });
	};
	const onDisplayContent = (value) => {
		setAttributes({ displayContent: value });
	};

	const onNumberOfItemsChange = (value) => {
		setAttributes({ numberOfPosts: value });
	};

	const onChangeStyle = (value) => {
		setAttributes({ style: value });
	};

	const onChangeBgColor = (value) => {
		setAttributes({ bgColor: value });
	};

	const onChangeTitleColor = (value) => {
		setAttributes({ titleColor: value });
	};

	const groupedEventPosts = {};
	eventPosts.forEach((eventPost) => {
		const start_date = eventPost.start_date;
		if (!groupedEventPosts[start_date]) {
			groupedEventPosts[start_date] = [];
		}
		groupedEventPosts[start_date].push(eventPost);
	});
	// Function to convert date to Finnish day name with d.m.Y format
	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("fi-FI", {
			weekday: "long", // Full day name in Finnish
			day: "numeric",
			month: "numeric",
			year: "numeric",
		});
	};

	const ReturnComponent = getActiveAddonComponent() || DefaultReturnComponent;

	const baseStyles = [
		{ label: __("Style 1", "artcloud-events"), value: "style1" },
	];

	const addonStyles =
		typeof artcloud_addon_events_styles !== "undefined"
			? artcloud_addon_events_styles.styles
			: [];

	const styleOptions = [...baseStyles, ...addonStyles];

	return (
		// <div className="wp-block-artcloud-event-block-event-blocks">
		<div {...useBlockProps()}>
			<InspectorControls>
				<PanelBody>
					<ToggleControl
						label={__("Display Time", "artcloud-events")}
						checked={displayTime}
						onChange={onDisplayTime}
					/>
					<ToggleControl
						label={__("Display Place", "artcloud-events")}
						checked={displayPlace}
						onChange={onDisplayPlace}
					/>
					<ToggleControl
						label={__("Display Content", "artcloud-events")}
						checked={displayContent}
						onChange={onDisplayContent}
					/>
					<PanelColorSettings
						title={__("Color Settings", "artcloud-events")}
						icon="admin-appearance"
						initialOpen
						colorSettings={[
							{
								value: titleColor,
								onChange: onChangeTitleColor,
								label: __("Title Color", "artcloud-events"),
							},
							{
								value: bgColor,
								onChange: onChangeBgColor,
								label: __("Background Color", "artcloud-events"),
							},
						]}
					/>
					<SelectControl
						label={__("Select Style", "artcloud-events")}
						value={style}
						options={styleOptions}
						onChange={onChangeStyle}
					/>
					<QueryControls
						numberOfItems={numberOfPosts}
						onNumberOfItemsChange={onNumberOfItemsChange}
						maxItems={200}
						minItems={1}
					/>
				</PanelBody>
			</InspectorControls>
			{
				eventPosts && eventPosts.length > 0 ? (
					style === "style1" ? (
						Object.keys(groupedEventPosts).map((start_date) => (
							<div className="event-block" key={start_date}>
								<hr />
								<h2>{formatDate(start_date)}</h2>
								<hr />
								{groupedEventPosts[start_date].map((eventPost) => (
									<div key={eventPost.id}>
										<h4>
											<a href={eventPost.link}>{eventPost.title.rendered}</a>
										</h4>
										{displayTime && (
											<p>
												{formatDate(eventPost.start_date)} Klo:
												{eventPost.start_time} - {eventPost.end_time}
											</p>
										)}
										{displayPlace && (
											<p>
												{__("Location:-", "artcloud-events")}
												{eventPost.address}
											</p>
										)}
										<div
											dangerouslySetInnerHTML={{
												__html: eventPost.excerpt.rendered,
											}}
										/>
									</div>
								))}
							</div>
						))
					) : (
						<ReturnComponent
							eventPosts={eventPosts}
							attributes={attributes}
							setAttributes={setAttributes}
						/>
					)
				) : (
					<p>{__("No event found.", "artcloud-events")}</p>
				)
			}

		</div>
	);
}

function DefaultReturnComponent() {
	return (
		<div {...useBlockProps()}>
			<p>{__("No custom addon plugin detected", "artcloud-events")}</p>
		</div>
	);
}
